/**
 * Coordinate resolvers that feed `buildPointSet`. Two sources of truth for a
 * row's lat/lng: coordinate-mode reads them straight from the row cells (no
 * geocode call), address-mode looks them up in M2's GeocodeResult set by
 * row_key. Both produce the same `ResolvedCoord` shape so the PointSet builder
 * is source-agnostic.
 */

import { parseCoords, type ColumnMapping, type ParsedRow } from "@/lib/ingest";
import type { GeocodeResult } from "@/lib/contracts/geocode";
import type { ResolvedCoord } from "./pointset";

/** Coordinate-mode: parse lat/lng from the mapped columns; confidence is 1 (passthrough). */
export function coordinateModeResolver(mapping: ColumnMapping): (row: ParsedRow) => ResolvedCoord | null {
  const latIdx = mapping.roles.lat;
  const lngIdx = mapping.roles.lng;
  return (row) => {
    if (latIdx === undefined || lngIdx === undefined) return null;
    const res = parseCoords(row.cells[latIdx] ?? "", row.cells[lngIdx] ?? "");
    return res.ok ? { lat: res.lat!, lng: res.lng!, confidence: 1 } : null;
  };
}

/** Address-mode: look up M2's geocoded coordinate + confidence by row_key. */
export function geocodeResultResolver(results: GeocodeResult[]): (row: ParsedRow) => ResolvedCoord | null {
  const byKey = new Map(results.map((r) => [r.row_key, r]));
  return (row) => {
    const r = byKey.get(row.row_key);
    if (!r || r.lat === null || r.lng === null) return null;
    return { lat: r.lat, lng: r.lng, confidence: r.confidence };
  };
}
